/**
 * Logica pura de recorrencia do Scheduler: presets intuitivos <->
 * payload da API ({kind, cron_expression, run_at}) + descricao legivel.
 * O CALCULO da proxima execucao e do backend (croniter, fonte da
 * verdade) -- aqui so traduzimos a forma.
 */

import type { TaskSchedule, TaskScheduleKind } from "@/api/types";
import { formatDateTime } from "@/lib/date";

export type RecurrencePreset =
  | { kind: "once"; runAt: string }
  | { kind: "daily"; time: string }
  | { kind: "weekly"; weekdays: number[]; time: string }
  | { kind: "monthly"; day: number; time: string }
  | { kind: "cron"; expression: string };

export interface RecurrencePayload {
  kind: TaskScheduleKind;
  cron_expression?: string;
  run_at?: string;
}

function splitTime(time: string): { hour: number; minute: number } {
  const [h = "0", m = "0"] = time.split(":");
  return { hour: Number(h), minute: Number(m) };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Preset -> payload da API. ``runAt`` (datetime-local) vira ISO. */
export function presetToPayload(preset: RecurrencePreset): RecurrencePayload {
  switch (preset.kind) {
    case "once":
      return {
        kind: "once",
        run_at: new Date(preset.runAt).toISOString(),
      };
    case "daily": {
      const { hour, minute } = splitTime(preset.time);
      return { kind: "cron", cron_expression: `${minute} ${hour} * * *` };
    }
    case "weekly": {
      const { hour, minute } = splitTime(preset.time);
      const days = [...preset.weekdays].sort((a, b) => a - b).join(",");
      return {
        kind: "cron",
        cron_expression: `${minute} ${hour} * * ${days}`,
      };
    }
    case "monthly": {
      const { hour, minute } = splitTime(preset.time);
      return {
        kind: "cron",
        cron_expression: `${minute} ${hour} ${preset.day} * *`,
      };
    }
    case "cron":
      return { kind: "cron", cron_expression: preset.expression };
  }
}

const DAILY_RE = /^(\d{1,2}) (\d{1,2}) \* \* \*$/;
const WEEKLY_RE = /^(\d{1,2}) (\d{1,2}) \* \* ([0-6](?:,[0-6])*)$/;
const MONTHLY_RE = /^(\d{1,2}) (\d{1,2}) (\d{1,2}) \* \*$/;

/**
 * Payload salvo -> preset para edicao. Cron fora dos padroes de preset
 * cai no modo avancado ({kind: "cron"}).
 */
export function payloadToPreset(schedule: {
  kind: TaskScheduleKind;
  cron_expression: string | null;
  run_at: string | null;
}): RecurrencePreset {
  if (schedule.kind === "once") {
    return { kind: "once", runAt: toDatetimeLocal(schedule.run_at) };
  }
  const expr = (schedule.cron_expression ?? "").trim();
  let m = expr.match(DAILY_RE);
  if (m) {
    return { kind: "daily", time: `${pad(Number(m[2]))}:${pad(Number(m[1]))}` };
  }
  m = expr.match(WEEKLY_RE);
  if (m) {
    return {
      kind: "weekly",
      weekdays: (m[3] ?? "").split(",").map(Number),
      time: `${pad(Number(m[2]))}:${pad(Number(m[1]))}`,
    };
  }
  m = expr.match(MONTHLY_RE);
  if (m) {
    return {
      kind: "monthly",
      day: Number(m[3]),
      time: `${pad(Number(m[2]))}:${pad(Number(m[1]))}`,
    };
  }
  return { kind: "cron", expression: expr };
}

/** ISO -> valor de <input type="datetime-local"> (hora local). */
export function toDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Recorrencia legivel: "Diariamente as 09:00", "Uma vez em ...". */
export function describeSchedule(
  schedule: Pick<TaskSchedule, "kind" | "cron_expression" | "run_at">,
  t: Translate,
): string {
  const preset = payloadToPreset(schedule);
  switch (preset.kind) {
    case "once":
      return t("recurrence.describe.once", {
        datetime: formatDateTime(schedule.run_at),
      });
    case "daily":
      return t("recurrence.describe.daily", { time: preset.time });
    case "weekly": {
      const days = preset.weekdays
        .map((d) => t(`recurrence.weekdays.${d}`))
        .join(", ");
      return t("recurrence.describe.weekly", { days, time: preset.time });
    }
    case "monthly":
      return t("recurrence.describe.monthly", {
        day: preset.day,
        time: preset.time,
      });
    case "cron":
      return t("recurrence.describe.cron", { expression: preset.expression });
  }
}
